import { useEffect, useState } from "react";

/**
 * Hook to get the campaign duration in days from localStorage.
 *
 * IMPORTANT: This must stay in sync with the Time step (date selection) which
 * updates localStorage and dispatches the `campaignDatesChanged` event.
 */
export const useCampaignDays = () => {
  const readTotalDaysFromStorage = () => {
    // Check the date selection mode first
    const selectionMode = localStorage.getItem("dateSelectionMode");
    
    // For specific dates mode, count the actual selected dates
    if (selectionMode === 'specific') {
      const selectedDatesStr = localStorage.getItem("selectedDates");
      if (selectedDatesStr) {
        try {
          const selectedDates = JSON.parse(selectedDatesStr);
          if (Array.isArray(selectedDates) && selectedDates.length > 0) {
            return selectedDates.length;
          }
        } catch {
          // Fall through to other methods
        }
      }
    }
    
    // For range mode or fallback: derive from start/end dates
    const startExact = localStorage.getItem("startDate_exact");
    const endExact = localStorage.getItem("endDate_exact");
    const startIso = localStorage.getItem("startDate");
    const endIso = localStorage.getItem("endDate");

    const startRaw = startExact || startIso;
    const endRaw = endExact || endIso;

    const toDateOnly = (raw: string) => {
      // raw may be YYYY-MM-DD or full ISO
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      const dt = new Date(raw);
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    };

    if (startRaw && endRaw) {
      const start = toDateOnly(startRaw);
      const end = toDateOnly(endRaw);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays > 0) return diffDays;
      }
    }

    // Fallback to stored duration
    const storedDuration = localStorage.getItem("duration");
    if (storedDuration) {
      const parsed = parseInt(storedDuration, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    // Default to 3 days
    return 3;
  };

  const [totalDays, setTotalDays] = useState<number>(() => readTotalDaysFromStorage());

  useEffect(() => {
    const sync = () => setTotalDays(readTotalDaysFromStorage());

    // Cross-tab changes
    window.addEventListener("storage", sync);
    // Same-tab changes from the Time step calendar
    window.addEventListener("campaignDatesChanged", sync as EventListener);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("campaignDatesChanged", sync as EventListener);
    };
  }, []);

  return { totalDays };
};
